import React, { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const empty = {
  name: "", phone: "", email: "",
  country_code: "AE", preferred_currency: "AED",
  preferred_channel: "WhatsApp",
  gdpr_consent: false, marketing_consent: false,
  referred_by: "",
};

export default function NewMemberDialog({ countries = [], rates = [], onCreated, buttonLabel = "New member" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, referred_by: form.referred_by.trim() || null };
      const { data } = await api.post("/customers", body);
      toast.success(`Enrolled · ${data.customer_id}`);
      setOpen(false);
      setForm(empty);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-customer-btn" className="bg-navy hover:opacity-90 text-white rounded-xl">
          <Plus className="h-4 w-4 mr-1.5" /> {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="new-customer-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Enrol a new member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   required data-testid="new-cust-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     required data-testid="new-cust-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     required data-testid="new-cust-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={form.country_code}
                      onValueChange={(v) => {
                        const c = countries.find(x => x.code === v);
                        setForm({ ...form, country_code: v,
                                  preferred_currency: c?.default_currency || form.preferred_currency });
                      }}>
                <SelectTrigger data-testid="new-cust-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}{c.gdpr_covered ? " · GDPR" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={form.preferred_currency}
                      onValueChange={(v) => setForm({ ...form, preferred_currency: v })}>
                <SelectTrigger data-testid="new-cust-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rates.map((r) => (
                    <SelectItem key={r.currency} value={r.currency}>{r.currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select value={form.preferred_channel}
                      onValueChange={(v) => setForm({ ...form, preferred_channel: v })}>
                <SelectTrigger data-testid="new-cust-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Referred by (optional)</Label>
              <Input placeholder="ZOLA-… or VENDOR-…"
                     value={form.referred_by}
                     onChange={(e) => setForm({ ...form, referred_by: e.target.value })}
                     data-testid="new-cust-referral" />
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-xl p-3 cursor-pointer"
                 style={{ background: "var(--bg-soft)" }}>
            <Switch checked={form.gdpr_consent}
                    onCheckedChange={(v) => setForm({ ...form, gdpr_consent: v })}
                    data-testid="new-cust-gdpr" />
            <div className="text-xs">
              <div className="font-medium">GDPR consent</div>
              <div style={{ color: "var(--text-muted)" }}>Required for members in GDPR-covered countries before any promo message.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 rounded-xl p-3 cursor-pointer"
                 style={{ background: "var(--bg-soft)" }}>
            <Switch checked={form.marketing_consent}
                    onCheckedChange={(v) => setForm({ ...form, marketing_consent: v })}
                    data-testid="new-cust-marketing" />
            <div className="text-xs">
              <div className="font-medium">Marketing consent</div>
              <div style={{ color: "var(--text-muted)" }}>Separate opt-in for promotional messages (WhatsApp / Email / SMS).</div>
            </div>
          </label>
          <DialogFooter>
            <Button type="submit" disabled={busy}
                    className="bg-navy hover:opacity-90 text-white rounded-xl"
                    data-testid="submit-new-cust">
              {busy ? "Creating…" : "Create member"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
